/**
 * DBN AI Executive Copilot — Executive Greeting + AI Recommendations + Workflows + Chat
 */
import React, { useState, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { dbnApi, PageHeader, SectionHeader, Btn, Badge, LoadingState } from "./dbnUtils";
import { Bot, Send, ChevronRight, TrendingUp, Shield, FileText, Users, Zap, AlertTriangle, CheckCircle, Clock, Leaf } from "lucide-react";

const WORKFLOWS = [
  { id:"portfolio_brief",   label:"Portfolio Executive Brief",    icon:TrendingUp, color:"#34d399",
    desc:"One-page executive summary of portfolio performance, capital deployment and development impact." },
  { id:"investment_memo",   label:"Investment Assessment Memo",   icon:FileText,   color:"#60a5fa",
    desc:"Draft an Investment Assessment Memorandum for a specific project in the portfolio.", needsId:true },
  { id:"board_paper",       label:"Board Paper",                  icon:Users,      color:"#a78bfa",
    desc:"Structured board paper on programme performance for the DBN Board of Directors." },
  { id:"pfi_analysis",      label:"PFI Performance Analysis",     icon:TrendingUp, color:"#fbbf24",
    desc:"Detailed performance analysis for a specific Participating Financial Institution.", needsId:true },
  { id:"risk_digest",       label:"Risk & Compliance Digest",     icon:Shield,     color:"#f97316",
    desc:"Summarise all open portfolio alerts, compliance issues and recommended actions." },
  { id:"carbon",            label:"Carbon Opportunity Report",    icon:Zap,        color:"#0d9488",
    desc:"Analysis of carbon credit pipeline, monetisation opportunities and revenue potential." },
  { id:"poa_portfolio",     label:"PoA Portfolio Intelligence",   icon:Leaf,       color:"#34d399",
    desc:"Executive summary of all Programmes of Activities, CPAs, issuance status and revenue." },
  { id:"poa_aggregation",   label:"PoA Aggregation Opportunities",icon:TrendingUp, color:"#0891b2",
    desc:"AI recommendations for project aggregation into new Programmes of Activities." },
];

const ENTITY_OPTIONS = {
  investment_memo: ["PRJ-001: Lagos Circular Plastics Recovery Hub","PRJ-002: Kano Solar Cold Chain Network",
    "PRJ-003: Ogun Low-Carbon Building Materials","PRJ-005: Abuja Electric Mobility Fleet","PRJ-012: Lagos Rooftop Solar Aggregation"],
  pfi_analysis: ["PFI-001: Access Bank Plc","PFI-002: Zenith Bank Plc","PFI-003: First Bank of Nigeria",
    "PFI-010: Bank of Agriculture","PFI-011: NIRSAL Microfinance Bank"],
};

const QUICK_CHAT = [
  "Which PFIs are lagging on deployment?",
  "What is the EIB covenant status?",
  "Which projects are carbon-eligible?",
  "Summarise IFRS S2 readiness gaps",
  "What are the high-severity alerts?",
  "Which sectors deliver the most impact per EUR?",
];

const PRIORITY_ICONS = {
  "alert-triangle": AlertTriangle, "trending-down": TrendingUp,
  "clipboard-check": CheckCircle, "git-branch": TrendingUp,
};

export default function DBNAICopilot() {
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [activeWorkflow, setActiveWorkflow] = useState(null);
  const [entityId, setEntityId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState(null);
  const [tab, setTab] = useState("command");
  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState([
    { role:"assistant", content:"Welcome to the DBN AI Portfolio Intelligence Copilot. I can generate executive reports, analyse portfolio performance, and provide actionable insights. Select a workflow or ask me anything about the portfolio." }
  ]);
  const [chatLoading, setChatLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    dbnApi.executiveSummary()
      .then(r => setExecutiveSummary(r.data))
      .catch(() => {})
      .finally(() => setSummaryLoading(false));
  }, []);

  const handleGenerate = async () => {
    if (!activeWorkflow) return;
    setGenerating(true); setOutput(null);
    try {
      // PoA workflows go to dedicated PoA AI endpoint
      const isPoA = activeWorkflow.id.startsWith("poa_");
      const payload = { workflow: isPoA ? activeWorkflow.id.replace("poa_","") : activeWorkflow.id,
        entity_id: entityId ? entityId.split(":")[0].trim() : undefined };
      const r = isPoA
        ? await dbnApi.poaAi(payload)
        : await dbnApi.aiGenerate(payload);
      setOutput(r.data);
    } catch(e) {}
    setGenerating(false);
  };

  const handleChat = async () => {
    if (!chatInput.trim()) return;
    const q = chatInput;
    setChatInput("");
    setChatHistory(prev => [...prev, { role:"user", content:q }]);
    setChatLoading(true);
    try {
      const r = await dbnApi.aiInsights(q);
      setChatHistory(prev => [...prev, {
        role:"assistant",
        content: r.data?.response || "I am analysing the portfolio data. Please try a more specific query.",
        confidence: r.data?.confidence,
        sources: r.data?.sources,
      }]);
    } catch(e) {
      setChatHistory(prev => [...prev, { role:"assistant", content:"Unable to fetch analysis. Please try again." }]);
    }
    setChatLoading(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(output?.content || "");
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadTxt = () => {
    const blob = new Blob([output?.content||""], { type:"text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href=url; a.download=`DBN-${output?.workflow||"output"}-${new Date().toISOString().slice(0,10)}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  const es = executiveSummary;
  const kpis = es?.kpis || {};
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PageHeader
        title="AI Copilot"
        subtitle="Executive command centre · Portfolio intelligence · Workflow generation · Q&A" />

      <div style={{ padding:"16px 24px", flex:1, display:"flex", flexDirection:"column", gap:16 }}>

        {/* ── EXECUTIVE COMMAND PANEL ── */}
        {!summaryLoading && es && (
          <div style={{ background:"linear-gradient(135deg,#07111e,#0a1e36)", border:"1px solid #1e3a5f",
            borderRadius:14, padding:"16px 20px" }}>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:14 }}>
              <div>
                <p style={{ fontSize:16, fontWeight:900, color:"#f0f9ff", marginBottom:4 }}>
                  {greeting}. <span style={{ color:"#34d399" }}>Here is your portfolio status.</span>
                </p>
                <p style={{ fontSize:10, color:"#64748b", maxWidth:600, lineHeight:1.6 }}>{es.ai_greeting}</p>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <div style={{ background:"#060f1e", borderRadius:8, padding:"8px 12px", textAlign:"center", minWidth:72 }}>
                  <p style={{ fontSize:16, fontWeight:900, color:"#34d399" }}>{kpis.deployment_rate}%</p>
                  <p style={{ fontSize:7, color:"#334155", textTransform:"uppercase" }}>Deployed</p>
                </div>
                <div style={{ background:"#060f1e", borderRadius:8, padding:"8px 12px", textAlign:"center", minWidth:72 }}>
                  <p style={{ fontSize:16, fontWeight:900, color:kpis.high_alerts>0?"#ef4444":"#22c55e" }}>{kpis.high_alerts}</p>
                  <p style={{ fontSize:7, color:"#334155", textTransform:"uppercase" }}>High Alerts</p>
                </div>
                <div style={{ background:"#060f1e", borderRadius:8, padding:"8px 12px", textAlign:"center", minWidth:72 }}>
                  <p style={{ fontSize:16, fontWeight:900, color:"#f59e0b" }}>{kpis.pending_assessments}</p>
                  <p style={{ fontSize:7, color:"#334155", textTransform:"uppercase" }}>Pending Reviews</p>
                </div>
                <div style={{ background:"#060f1e", borderRadius:8, padding:"8px 12px", textAlign:"center", minWidth:72 }}>
                  <p style={{ fontSize:16, fontWeight:900, color:"#60a5fa" }}>{kpis.active_projects}</p>
                  <p style={{ fontSize:7, color:"#334155", textTransform:"uppercase" }}>Active Projects</p>
                </div>
              </div>
            </div>

            {/* Today's Priorities */}
            {es.priorities?.length > 0 && (
              <div>
                <p style={{ fontSize:9, color:"#334155", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:8 }}>
                  Today's Priority Actions
                </p>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {es.priorities.map((p, i) => {
                    const Icon = PRIORITY_ICONS[p.icon] || AlertTriangle;
                    return (
                      <a key={i} href={p.link}
                        style={{ background:"#060f1e", border:`1px solid ${p.color}30`, borderRadius:10,
                          padding:"8px 12px", display:"flex", alignItems:"flex-start", gap:8, flex:1,
                          minWidth:200, maxWidth:320, textDecoration:"none" }}>
                        <Icon size={13} color={p.color} style={{ flexShrink:0, marginTop:1 }} />
                        <div>
                          <p style={{ fontSize:10, fontWeight:700, color:"#f0f9ff", marginBottom:2 }}>{p.title}</p>
                          <p style={{ fontSize:8.5, color:"#475569", lineHeight:1.4 }}>{p.body}</p>
                          <p style={{ fontSize:8, color:p.color, marginTop:4, fontWeight:600 }}>{p.action} →</p>
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display:"flex", gap:2, background:"#0a1628",
          border:"1px solid #0f2033", borderRadius:10, padding:4, width:"fit-content" }}>
          {[
            {key:"command",   label:"Executive Workflows"},
            {key:"chat",      label:"Portfolio Q&A"},
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding:"7px 16px", borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontWeight:700,
                background:tab===t.key?"rgba(52,211,153,0.15)":"transparent",
                color:tab===t.key?"#34d399":"#475569" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Workflows */}
        {tab === "command" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1.5fr", gap:16 }}>
            <div>
              <p style={{ fontSize:11, color:"#64748b", fontWeight:700, textTransform:"uppercase",
                letterSpacing:"0.1em", marginBottom:10 }}>Available Workflows</p>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {WORKFLOWS.map(w => (
                  <div key={w.id}
                    onClick={() => { setActiveWorkflow(w); setOutput(null); setEntityId(""); }}
                    data-testid={`workflow-${w.id}`}
                    style={{
                      background:activeWorkflow?.id===w.id?"rgba(52,211,153,0.08)":"#0a1628",
                      border:`1px solid ${activeWorkflow?.id===w.id?w.color:"#0f2033"}`,
                      borderRadius:12, padding:"10px 14px", cursor:"pointer", transition:"all .15s"
                    }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                      <w.icon size={13} color={w.color} />
                      <span style={{ fontSize:11, fontWeight:700, color:"#f0f9ff" }}>{w.label}</span>
                    </div>
                    <p style={{ fontSize:9, color:"#475569", lineHeight:1.4 }}>{w.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              {activeWorkflow ? (
                <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
                    <activeWorkflow.icon size={16} color={activeWorkflow.color} />
                    <p style={{ fontSize:14, fontWeight:800, color:"#f0f9ff" }}>{activeWorkflow.label}</p>
                  </div>
                  <p style={{ fontSize:10, color:"#64748b", marginBottom:14, lineHeight:1.5 }}>{activeWorkflow.desc}</p>

                  {activeWorkflow.needsId && ENTITY_OPTIONS[activeWorkflow.id] && (
                    <div style={{ marginBottom:14 }}>
                      <label style={{ fontSize:10, color:"#64748b", fontWeight:700, display:"block", marginBottom:6 }}>
                        Select {activeWorkflow.id === "investment_memo" ? "Project" : "PFI"}
                      </label>
                      <select value={entityId} onChange={e => setEntityId(e.target.value)}
                        style={{ width:"100%", background:"#060f1e", border:"1px solid #1e3a5f",
                          borderRadius:8, padding:"8px 12px", fontSize:11, color:"#f0f9ff" }}>
                        <option value="">Select…</option>
                        {ENTITY_OPTIONS[activeWorkflow.id].map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <Btn onClick={handleGenerate} color={activeWorkflow.color}
                    disabled={generating || (activeWorkflow.needsId && !entityId)}>
                    {generating ? "Generating…" : `Generate ${activeWorkflow.label}`}
                  </Btn>

                  {generating && (
                    <div style={{ textAlign:"center", padding:"24px 0" }}>
                      <div style={{ width:28, height:28, border:"3px solid #0f2033", borderTopColor:"#34d399",
                        borderRadius:"50%", animation:"spin 1s linear infinite", margin:"0 auto 10px" }} />
                      <p style={{ fontSize:11, color:"#475569" }}>Generating institutional document…</p>
                    </div>
                  )}

                  {output && (
                    <div style={{ marginTop:16 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                        <Badge label="Generated" color="#34d399" />
                        <div style={{ display:"flex", gap:6 }}>
                          <Btn small outline color="#475569" onClick={handleCopy}>
                            {copied ? "Copied!" : "Copy"}
                          </Btn>
                          <Btn small outline color="#60a5fa" onClick={handleDownloadTxt}>
                            Download .txt
                          </Btn>
                        </div>
                      </div>
                      <pre style={{
                        background:"#060f1e", borderRadius:10, padding:"14px 16px",
                        fontSize:9.5, color:"#94a3b8", lineHeight:1.6, whiteSpace:"pre-wrap",
                        fontFamily:"'Courier New', monospace", maxHeight:400, overflowY:"auto",
                        border:"1px solid #0f2033"
                      }}>{output.content}</pre>
                      <p style={{ fontSize:8, color:"#334155", marginTop:6 }}>
                        Generated by DeCarbonIQ AI on {output.generated_at?.slice(0,10)} · Demo — synthetic data only
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:32,
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center" }}>
                  <Bot size={40} color="#1e3a5f" style={{ marginBottom:12 }} />
                  <p style={{ fontSize:13, color:"#334155", fontWeight:600 }}>Select a workflow to get started</p>
                  <p style={{ fontSize:10, color:"#1e3a5f", marginTop:6 }}>
                    Generate executive-grade documents from portfolio data in seconds
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Chat */}
        {tab === "chat" && (
          <div style={{ display:"grid", gridTemplateColumns:"1.2fr 1fr", gap:16, flex:1 }}>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, overflow:"hidden",
              display:"flex", flexDirection:"column" }}>
              <div style={{ flex:1, overflowY:"auto", padding:"16px", display:"flex", flexDirection:"column", gap:12, maxHeight:480 }}>
                {chatHistory.map((msg, i) => (
                  <div key={i} style={{ alignSelf:msg.role==="user"?"flex-end":"flex-start", maxWidth:"85%" }}>
                    <div style={{
                      background:msg.role==="user"?"#1e3a5f":"#060f1e",
                      borderRadius:12, padding:"10px 14px",
                      border:`1px solid ${msg.role==="user"?"#1e5a8f":"#0f2033"}`
                    }}>
                      {msg.role==="assistant" && (
                        <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:5 }}>
                          <Bot size={10} color="#34d399" />
                          <span style={{ fontSize:8, color:"#34d399", fontWeight:700, textTransform:"uppercase" }}>AI Copilot</span>
                          {msg.confidence && <span style={{ fontSize:8, color:"#1e3a5f" }}>· {Math.round(msg.confidence*100)}%</span>}
                        </div>
                      )}
                      <p style={{ fontSize:10, color:msg.role==="user"?"#bfdbfe":"#94a3b8", lineHeight:1.6 }}>{msg.content}</p>
                      {msg.sources?.length > 0 && (
                        <div style={{ marginTop:6, display:"flex", flexWrap:"wrap", gap:4 }}>
                          {msg.sources.map(s => (
                            <span key={s} style={{ fontSize:7, background:"#0f2033", color:"#334155",
                              padding:"1px 5px", borderRadius:3 }}>{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div style={{ alignSelf:"flex-start" }}>
                    <div style={{ background:"#060f1e", borderRadius:12, padding:"10px 14px",
                      border:"1px solid #0f2033", display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ display:"flex", gap:3 }}>
                        {[0,1,2].map(i => (
                          <div key={i} style={{ width:4, height:4, borderRadius:"50%", background:"#34d399",
                            animation:`bounce 1s ease-in-out ${i*0.15}s infinite` }} />
                        ))}
                      </div>
                      <span style={{ fontSize:9, color:"#475569" }}>Analysing portfolio data…</span>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ padding:"12px 16px", borderTop:"1px solid #0f2033" }}>
                <div style={{ display:"flex", gap:8 }}>
                  <input value={chatInput} onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => e.key==="Enter" && !e.shiftKey && handleChat()}
                    placeholder="Ask about portfolio performance, PFIs, risks, carbon opportunities…"
                    style={{ flex:1, background:"#060f1e", border:"1px solid #1e3a5f", borderRadius:10,
                      padding:"9px 14px", fontSize:11, color:"#f0f9ff" }} />
                  <Btn color="#34d399" onClick={handleChat} disabled={chatLoading||!chatInput.trim()}>
                    <Send size={12} />
                  </Btn>
                </div>
              </div>
            </div>

            <div>
              <p style={{ fontSize:11, color:"#64748b", fontWeight:700, textTransform:"uppercase",
                letterSpacing:"0.1em", marginBottom:10 }}>Suggested Questions</p>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {QUICK_CHAT.map((q, i) => (
                  <button key={i} onClick={() => setChatInput(q)}
                    style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:10,
                      padding:"9px 14px", textAlign:"left", cursor:"pointer", fontSize:10,
                      color:"#64748b", transition:"all .15s", display:"flex", alignItems:"center", gap:6 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor="#1e3a5f"; e.currentTarget.style.color="#94a3b8"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor="#0f2033"; e.currentTarget.style.color="#64748b"; }}>
                    <ChevronRight size={10} />{q}
                  </button>
                ))}
              </div>
              <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:12, padding:14, marginTop:16 }}>
                <p style={{ fontSize:10, color:"#475569", fontWeight:700, marginBottom:6 }}>About This Copilot</p>
                <p style={{ fontSize:9, color:"#334155", lineHeight:1.6 }}>
                  The DBN AI Copilot analyses 24 projects, 12 PFIs, 4 facilities and 8 TA programmes
                  to provide contextual, data-driven answers and generate institutional documents.
                  All outputs are based on synthetic demo data.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
